import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth.store';
import { getSocket, closeSocket } from '@/features/realtime/socket';
import { useRealtimeWorkflow } from '@/features/realtime/hooks/useRealtimeWorkflow';
import { NotificationToast } from '../components/NotificationToast';
import type {
  NotificationItem,
  PaginatedNotifications,
} from '../services/notifications.service';

const NOTIFICATIONS_KEY = ['notifications'];

/**
 * Owns the single Socket.IO connection for the authenticated portal. On each
 * incoming `notification` event it updates the notifications cache (so the bell
 * badge stays live) and shows a top-center push toast.
 *
 * Mount this ONCE (via <RealtimeNotifications/> in DashboardLayout). Because the
 * connection lives only inside the portal layout, it never runs on the login
 * page, and keeping it single avoids the duplicate-socket/duplicate-cache bug
 * that existed when the socket was created inside useNotifications().
 */
// Not exported: keeping only the component export lets Fast Refresh work for
// this module. It's consumed solely via <RealtimeNotifications/> below.
function useRealtimeNotifications() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const qc = useQueryClient();
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;

    // Shared portal socket — reused by the workflow/dashboard listener too.
    const socket = getSocket(accessToken);

    const handleNotification = (incoming: NotificationItem) => {
      if (!incoming.id) return;

      // The backend emits each notification twice (room broadcast + a direct
      // per-socket fallback), so dedupe by id before touching cache / toasting.
      if (seenRef.current.has(incoming.id)) return;
      seenRef.current.add(incoming.id);
      if (seenRef.current.size > 200) {
        seenRef.current = new Set(Array.from(seenRef.current).slice(-100));
      }

      qc.setQueryData<PaginatedNotifications>(NOTIFICATIONS_KEY, (prev) => {
        if (!prev) return prev;
        if (prev.data.some((n) => n.id === incoming.id)) return prev;
        return { ...prev, data: [incoming, ...prev.data], total: prev.total + 1 };
      });

      toast.custom(
        (toastInstance) => (
          <NotificationToast
            toastInstance={toastInstance}
            notification={incoming}
          />
        ),
        { position: 'top-center', duration: 6000 },
      );
    };

    socket.on('notification', handleNotification);

    // Only detach this hook's listener on cleanup; the shared socket is torn
    // down by the unauth effect below (so the workflow listener keeps working).
    return () => {
      socket.off('notification', handleNotification);
    };
  }, [isAuthenticated, accessToken, qc]);

  // Close the shared socket when the session ends.
  useEffect(() => {
    if (!isAuthenticated) closeSocket();
  }, [isAuthenticated]);
}

/**
 * Headless mount point for the portal's real-time listeners: notifications +
 * workflow/dashboard state changes (M2 §12.1). Mount ONCE (DashboardLayout).
 */
export function RealtimeNotifications() {
  useRealtimeNotifications();
  useRealtimeWorkflow();
  return null;
}
